import {
  Add,
  Collections,
  Delete,
  Edit,
  Image,
  Info,
  Loop,
  SwapHoriz,
} from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "_metronic/i18n/language";
import { QuestionApiService } from "app/services/Ananse/QuestionApiService";
import {
  CognitiveCategoryType,
  CollectionType,
  FileType,
  QuestionType,
  QuizType,
} from "app/types";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { Box } from "app/pages/admin/components";
import FrameModal from "../FrameModal";
import ArrangeSequenceModal from "../../Questions/ArrangeSequenceModal";
import { selectInput } from "../../../Anamnese/mock";
import { Tooltip } from "@material-ui/core";
import DataTable from "app/pages/admin/components/DataTable";
import { QUIZ_TYPES_CONTANTS } from "app/types/quiz-constants";
import { LocaliseAPIService } from "app/services/Localise/localiseApi";
import useQuestionsListModel from "../../Questions/hooks/useQuestionsListModel";
import Swal from "sweetalert2";
import {
  getQuestionLabel,
  rotateQuestionPackages,
} from "../../utils/cloneQuestionPackages";

interface QuestionsCloneListPropsd {
  questions: QuestionType[];
  setQuestions: Dispatch<SetStateAction<QuestionType[]>>;
  collections: CollectionType[];
  quiz: QuizType;
  onAdd: () => void;
  onDelete: (questionId: number) => void;
  onEdit: (question: QuestionType) => void;
}

const localiseApiService = LocaliseAPIService.getInstance();

export const getQuestionType = (value: string) => {
  const findType = selectInput.find(v => v.type === value);
  return findType?.name || "N/A";
};

export const getFragrance = (
  fragranceId: any,
  collections: CollectionType[],
) => {
  if (!collections.length) {
    return fragranceId;
  }
  const fragrance = collections.find(
    collection => collection.fragranceId == fragranceId,
  );
  return fragrance?.fragrance.name || "N/A";
};

const QuestionsCloneList: FC<QuestionsCloneListPropsd> = ({
  questions,
  collections,
  quiz,
  setQuestions,
  onEdit,
  onDelete,
  onAdd,
}) => {
  const translate = useTranslation();
  const [cognitiveCategories, setCognitiveCategories] = useState<
    CognitiveCategoryType[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const questionService = new QuestionApiService();
  const { getFragranceById } = useQuestionsListModel();

  useEffect(() => {
    const start = async () => {
      const data = await localiseApiService.getCategories();
      if (data) {
        setCognitiveCategories(data);
      }
    };
    start();
  }, []);

  useEffect(() => {
    const availableIds = new Set(
      questions.map(question => question.quizQuestionId),
    );
    setSelectedQuestionIds(current =>
      current.filter(questionId => availableIds.has(questionId)),
    );
  }, [questions]);

  const toggleQuestionSelection = (questionId: number) => {
    setSelectedQuestionIds(current =>
      current.includes(questionId)
        ? current.filter(id => id !== questionId)
        : [...current, questionId],
    );
  };

  const redistributePackages = async (directSwap: boolean) => {
    if (selectedQuestionIds.length < 2) {
      return;
    }

    if (directSwap && selectedQuestionIds.length !== 2) {
      return;
    }

    const selectedSet = new Set(selectedQuestionIds);
    const selectedQuestions = questions.filter(question =>
      selectedSet.has(question.quizQuestionId),
    );
    const preview = selectedQuestions.map((destination, index) => {
      const source = selectedQuestions[(index + 1) % selectedQuestions.length];
      return `${destination.arrangeQuestionSequence ||
        index + 1} - ${getQuestionLabel(
        destination,
      )} recebe o conteúdo de ${source.arrangeQuestionSequence ||
        index + 1} - ${getQuestionLabel(source)}`;
    });

    const result = await Swal.fire({
      title: directSwap
        ? "Trocar conteúdo das perguntas?"
        : "Redistribuir conteúdo das perguntas?",
      text: preview.join("\n"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: translate("buttons.confirm"),
      cancelButtonText: translate("buttons.cancel"),
    });

    if (!result.isConfirmed) {
      return;
    }

    setQuestions(current =>
      rotateQuestionPackages(current, selectedQuestionIds),
    );
    setSelectedQuestionIds([]);

    await Swal.fire({
      title: translate("defaultMessages_success"),
      text: directSwap
        ? "Os pacotes completos foram trocados. As labels e posições foram mantidas."
        : "Os pacotes completos foram redistribuídos. As labels e posições foram mantidas.",
      icon: "success",
    });
  };

  const handleMouseEnter = async (question: QuestionType) => {
    setLoading(true);
    // setModalTitle(question?.question?.quest || "");
    // setOpenModal(true);
    const images: FileType[] = [];

    for (const answer of question.answers) {
      const response = await questionService.getFileByFileOwnerId(
        answer.quizQuestionAnswerId.toString(),
      );
      const imageData = response[0];
      if (imageData && imageData.tempUri) {
        images.push(imageData);
      }
    }

    // setModalImages(images);
    setLoading(false);
  };

  const getCategoryCognitiveBy = (id: number): string => {
    if (!id) {
      return "N/A";
    }
    const cognitiveCategory = cognitiveCategories.find(
      category => category.categoryCognitiveQuestionId === id,
    );
    return cognitiveCategory?.name || "N/A";
  };

  const headColumns = [
    { id: "none", label: translate("screens.dashboard.action") },
    { id: "arrangeQuestionSequence", label: translate("labels_sequence") },
    { id: "question.quest", label: translate("screens_quiz_list_questions") },
    { id: "none", label: translate("labels_answers") },
    {
      id: "rightAnswer",
      label: translate("screenApp_results_rightResponse"),
    },
    {
      id: "fragranceId",
      label: ` ${
        +quiz.type === QUIZ_TYPES_CONTANTS.COGNITIVE
          ? translate("screens_collectionDevice_table_cognitive")
          : translate("screens_collectionDevice_table_fragrance")
      }`,
    },
    { id: "type", label: translate("screenApp_quiz_questionType") },
    { id: "createdAt", label: translate("screens_quiz_list_created") },
    { id: "updatedAt", label: translate("screens_quiz_list_updated") },
  ];

  const formatRows = (question: QuestionType, labelId: any) => {
    return (
      <TableRow key={`${labelId}-${question.questionId}`}>
        <TableCell>
          <Checkbox
            checked={selectedQuestionIds.includes(question.quizQuestionId)}
            onChange={() => toggleQuestionSelection(question.quizQuestionId)}
            inputProps={{
              "aria-label": `Selecionar ${getQuestionLabel(question)}`,
            }}
          />
          <IconButton
            title={translate("buttons_edit")}
            onClick={() => onEdit(question)}
          >
            <Edit />
          </IconButton>
          <IconButton
            title={translate("deleteRegister_delete")}
            onClick={() => onDelete(question.quizQuestionId)}
          >
            <Delete />
          </IconButton>
        </TableCell>
        <TableCell>{question.arrangeQuestionSequence || "-"}</TableCell>
        <TableCell>
          <div style={{ display: "flex", gap: "5px" }}>
            {question?.question?.description && (
              <Tooltip title={question?.question?.description}>
                <Info />
              </Tooltip>
            )}
            {question?.files?.length && (
              <Tooltip
                title={
                  question?.files[0]?.tempUri ? (
                    <img
                      style={{ width: "200px", height: "auto" }}
                      src={question?.files[0].tempUri || ""}
                      alt={question?.question?.quest || ""}
                    />
                  ) : (
                    <></>
                  )
                }
              >
                <Image />
              </Tooltip>
            )}
            {question?.question?.quest || "-"}
          </div>
        </TableCell>
        <TableCell>
          {question.answers.map((answer, index) => (
            <Typography
              key={`${question.quizQuestionId}-${answer.answerId}-${index}`}
            >
              {question.type === "4"
                ? `${answer.answer?.label} / ${getFragrance(
                    answer.answer?.description,
                    collections,
                  )}`
                : answer.answer
                ? answer.answer?.label
                : answer.desc}
              {answer.answer?.files?.length &&
                answer.answer?.files[0].tempUri && (
                  <img
                    style={{ width: "20px", height: "auto" }}
                    src={answer.answer?.files[0].tempUri || ""}
                    alt={question?.question?.quest || ""}
                  />
                )}
            </Typography>
          ))}
          {question.type === "image" && (
            <IconButton
              title="Visualizar imagens"
              onClick={() => handleMouseEnter(question)}
            >
              <Collections />
            </IconButton>
          )}
        </TableCell>
        <TableCell>{question.rightAnswer}</TableCell>
        <TableCell>
          {+quiz.type === QUIZ_TYPES_CONTANTS.COGNITIVE
            ? getCategoryCognitiveBy(
                Number(question?.categoryCognitiveQuestionId),
              )
            : getFragranceById(collections, +question.fragranceId)}
        </TableCell>
        <TableCell>{getQuestionType(question.type)}</TableCell>
        <TableCell>
          {question.createdAt
            ? new Date(question?.createdAt).toLocaleDateString("pt-BR", {
                day: "numeric",
                month: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
              })
            : ""}
        </TableCell>
        <TableCell>
          {question.updatedAt
            ? new Date(question.updatedAt).toLocaleDateString("pt-BR", {
                day: "numeric",
                month: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
              })
            : ""}
        </TableCell>
      </TableRow>
    );
  };

  const AdicionalButtonsToolBar = () => {
    return (
      <Box style={{ minWidth: 0, padding: 0 }}>
        <FrameModal quiz={quiz} />
        <ArrangeSequenceModal
          onSave={(questions: QuestionType[]) => {
            setQuestions(questions);
          }}
          questions={questions}
          returnArray={true}
        />
        <IconButton
          title="Trocar pacotes completos entre duas perguntas"
          disabled={selectedQuestionIds.length !== 2}
          onClick={() => redistributePackages(true)}
        >
          <SwapHoriz />
        </IconButton>
        <IconButton
          title="Redistribuir pacotes completos entre as perguntas selecionadas"
          disabled={selectedQuestionIds.length < 2}
          onClick={() => redistributePackages(false)}
        >
          <Loop />
        </IconButton>
        <IconButton
          title={translate("screens_quiz_labels_addQuestion")}
          onClick={onAdd}
        >
          <Add />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <DataTable
        formatRows={formatRows}
        headColumns={headColumns}
        loading={loading}
        rows={questions}
        translate={translate}
        additionalButtonsContainer={<AdicionalButtonsToolBar />}
        stickyToolBar={true}
        stickyToolBarTop={0}
      />
    </>
  );
};

export default QuestionsCloneList;
